import { Form, Layout } from 'antd';
import dayjs from 'dayjs';
import appointmentSelectors from 'features/appointment/services/selectors';
import { DATE_FORMAT } from 'features/appointment/utils/format';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { BookingV4ActionsProvider } from './context';
import BookingV4UI from './index.ui';
import ModalEditStartTime, { ModalEditStartTimeRef } from './ModalEditStartTime';
import ModalServicePicker, { ModalServicePickerRef } from './ModalServicesPicker';
import ModalStaffPicker, { ModalStaffPickerRef } from './ModalStaffPicker';
import ModalTimePicker, { ModalTimePickerRef } from './ModalTimePicker';
import { IBookingV4FormValues } from './types';
import { useBookingActions } from './useBookingActions';

type IBookingV4Props = {};
type IBookingV4Ref = {
  toggle: () => void;
  fetchStaffs: (date: string) => void;
  close: () => void;
};
const initialValues: IBookingV4FormValues = {
  date: dayjs(),
  customer: null,
  groupNumber: 1,
  staffs: [],
  note: '',
};
export const BookingV4Ref = React.createRef<IBookingV4Ref>();
const BookingV4Context = forwardRef<IBookingV4Ref, IBookingV4Props>((props, ref) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const masterData = appointmentSelectors.paramsMasterData();

  const handleClose = useCallback<IBookingV4Ref['close']>(() => setOpen(false), []);
  const { actions, submit } = useBookingActions(form, handleClose);

  const fetchStaffs = useCallback((date: string) => {
    ModalStaffPickerRef.current?.fetchStaffs(date);
  }, []);

  const handleToggle: IBookingV4Ref['toggle'] = () => {
    form.resetFields();
    // Default the booking date to the calendar's active date (not just today).
    form.setFieldValue('date', masterData?.date ? dayjs(masterData.date, DATE_FORMAT) : dayjs());
    setOpen((prev) => !prev);
  };

  useImperativeHandle(ref, () => ({
    toggle: handleToggle,
    close: handleClose,
    fetchStaffs: fetchStaffs,
  }));

  return (
    <Layout.Sider collapsed={!open} width="25%" style={siderStyle} collapsedWidth={0}>
      <Form initialValues={initialValues} style={{ height: '100%' }} layout="vertical" form={form} onFinish={submit}>
        <BookingV4ActionsProvider value={actions}>
          <BookingV4UI onClose={handleClose} />
        </BookingV4ActionsProvider>
      </Form>
      <ModalStaffPicker ref={ModalStaffPickerRef} />
      <ModalTimePicker ref={ModalTimePickerRef} />
      <ModalServicePicker ref={ModalServicePickerRef} />
      <ModalEditStartTime ref={ModalEditStartTimeRef} />
    </Layout.Sider>
  );
});

BookingV4Context.displayName = 'BookingV4Context';
const BookingV4 = {
  Portal: () => <BookingV4Context ref={BookingV4Ref} />,
  toggle: () => BookingV4Ref.current?.toggle(),
  close: () => BookingV4Ref.current?.close(),
  fetchStaffs: (date: string) => BookingV4Ref.current?.fetchStaffs(date),
};

export default BookingV4;

const siderStyle: React.CSSProperties = {
  background: '#fff',
};
